import { useState, useCallback } from 'react';
import { UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import type { CreateInvoiceForm } from '../Types/createInvoiceFormTypes';
import { InvoiceScheme, VatRateType } from '../Types/invoicesTypes';
import {
  shouldUseVatEU,
  getSuggestedVatType,
  getVatSuggestionMessage,
} from '../utils/crossBorderVatHelper';
import { getAllowedVatTypesForInvoice } from '../utils/vatMappings';
import { normalizeInvoiceScheme, normalizeInvoiceType } from '../utils/invoiceEnumNormalization';
import { invoiceLogger } from '../utils/invoiceLogger';

export interface UseCrossBorderVatSuggestionOptions {
  setValue: UseFormSetValue<CreateInvoiceForm>;
  getValues: UseFormGetValues<CreateInvoiceForm>;
  autoApply?: boolean; // If true, automatically applies VatEU to all items
}

export interface CrossBorderVatSuggestion {
  shouldSuggest: boolean;
  suggestedVatType: VatRateType | null;
  message: string | null;
  applyToAllItems: () => void;
  checkAndApply: () => void;
}

/**
 * Hook for handling cross-border VAT auto-suggestion.
 * Monitors buyerNip, currency, and invoiceType changes.
 * Optionally auto-applies VatEU to all invoice items.
 */
export const useCrossBorderVatSuggestion = (
  options: UseCrossBorderVatSuggestionOptions
): CrossBorderVatSuggestion => {
  const { setValue, getValues, autoApply = true } = options;

  const [shouldSuggest, setShouldSuggest] = useState(false);
  const [suggestedVatType, setSuggestedVatType] = useState<VatRateType | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  /**
   * Applies suggested VAT type to all invoice items.
   */
  const applyToAllItems = useCallback(() => {
    const items = getValues('items') || [];
    const suggested = suggestedVatType;

    if (!suggested || items.length === 0) return;

    invoiceLogger.debug('[useCrossBorderVatSuggestion] Applying VatEU to all items:', {
      suggestedVatType: suggested,
      itemCount: items.length,
    });

    items.forEach((_, index) => {
      setValue(`items.${index}.vatType`, suggested, {
        shouldValidate: false,
        shouldDirty: true,
      });
    });
  }, [getValues, setValue, suggestedVatType]);

  /**
   * Checks if VatEU should be suggested and applies it.
   * Call this method when buyerNip, currency, or type changes.
   * Memoized to prevent infinite loops.
   */
  const checkAndApply = useCallback(() => {
    const buyerNip = getValues('buyerNip');
    const currency = getValues('currency');
    const invoiceType = normalizeInvoiceType(getValues('type'));
    const scheme = normalizeInvoiceScheme(getValues('scheme'));

    if (scheme === InvoiceScheme.TravelMargin) {
      invoiceLogger.debug(
        '[useCrossBorderVatSuggestion] Skipping suggestion for TravelMargin scheme (VatMargin enforced)'
      );
      setShouldSuggest(false);
      setSuggestedVatType(null);
      setMessage(null);
      return;
    }

    const suggested = getSuggestedVatType(currency, buyerNip, invoiceType);
    const allowedVatTypes = getAllowedVatTypesForInvoice(scheme, invoiceType);
    const normalizedSuggested = suggested ?? null;
    const isSuggestedAllowed =
      normalizedSuggested != null && allowedVatTypes.includes(normalizedSuggested);

    if (normalizedSuggested && !isSuggestedAllowed) {
      invoiceLogger.warn(
        '[useCrossBorderVatSuggestion] Suggested VAT type is not allowed for current scheme/type; suggestion skipped',
        {
          scheme,
          invoiceType,
          suggestedVatType: normalizedSuggested,
          allowedVatTypes,
        }
      );
    }

    const effectiveSuggestion = isSuggestedAllowed ? normalizedSuggested : null;
    const shouldSuggestVat = shouldUseVatEU(currency, buyerNip, invoiceType);
    const suggestionMessage = getVatSuggestionMessage(buyerNip, effectiveSuggestion);

    invoiceLogger.debug('[useCrossBorderVatSuggestion] Check result:', {
      buyerNip,
      currency,
      invoiceType,
      scheme,
      suggested,
      effectiveSuggestion,
      allowedVatTypes,
      shouldSuggestVat,
      suggestionMessage,
    });

    setShouldSuggest(shouldSuggestVat && !!effectiveSuggestion);
    setSuggestedVatType(effectiveSuggestion);
    setMessage(suggestionMessage);

    // Auto-apply if enabled and suggestion exists
    if (autoApply && effectiveSuggestion) {
      const items = getValues('items') || [];

      invoiceLogger.debug('[useCrossBorderVatSuggestion] Items count:', items.length);

      if (items.length > 0) {
        // Check if items already have the suggested VAT type (prevent unnecessary updates)
        const needsUpdate = items.some((item) => item.vatType !== suggested);

        invoiceLogger.debug('[useCrossBorderVatSuggestion] Needs update:', needsUpdate);

        if (needsUpdate) {
          invoiceLogger.debug('[useCrossBorderVatSuggestion] Applying suggested VAT type to items');

          // CRITICAL FIX: Batch all setValue calls into ONE microtask
          // This prevents multiple re-renders that cause focus loss
          requestAnimationFrame(() => {
            items.forEach((item, index) => {
              // Only update if different (prevent infinite loop)
              if (item.vatType !== suggested) {
                const currentIsAllowed = allowedVatTypes.includes(item.vatType);
                if (!currentIsAllowed) {
                  invoiceLogger.warn(
                    '[useCrossBorderVatSuggestion] Current vatType is already outside allowed set; forcing valid suggestion',
                    {
                      index,
                      currentVatType: item.vatType,
                      allowedVatTypes,
                      scheme,
                      invoiceType,
                    }
                  );
                }

                invoiceLogger.debug(
                  `[useCrossBorderVatSuggestion] Updating item ${index}: ${item.vatType} → ${effectiveSuggestion}`
                );
                setValue(`items.${index}.vatType`, effectiveSuggestion, {
                  shouldValidate: false, // Disable validation
                  shouldDirty: false, // CRITICAL: Don't mark as dirty (prevents re-render)
                  shouldTouch: false, // CRITICAL: Don't mark as touched (prevents re-render)
                });
              }
            });
          });
        } else {
          invoiceLogger.debug(
            '[useCrossBorderVatSuggestion] Items already have suggested VAT type, skipping update'
          );
        }
      } else {
        invoiceLogger.warn(
          '[useCrossBorderVatSuggestion] No items found - VAT suggestion will apply when items are added'
        );
      }
    } else if (!autoApply) {
      invoiceLogger.debug('[useCrossBorderVatSuggestion] Auto-apply is disabled');
    } else if (!effectiveSuggestion) {
      invoiceLogger.debug(
        '[useCrossBorderVatSuggestion] No VAT suggestion (domestic or non-EU transaction)'
      );
    }
  }, [getValues, setValue, autoApply]);

  return {
    shouldSuggest,
    suggestedVatType,
    message,
    applyToAllItems,
    checkAndApply, // Export to allow manual triggering
  };
};
